import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  ChevronLeft,
  ChevronRight,
  Home,
  Mic,
  Headphones,
  BookOpen,
  TrendingUp,
  Users,
  Settings,
  LogOut,
  Crown,
  Sparkles,
  Globe,
  LifeBuoy,
  Radio,
  Music,
  Calendar,
  Heart,
  Download,
  Play,
  Pause,
  Volume2,
  Upload,
  FolderOpen,
  File,
  Folder,
  FileAudio,
  HardDrive
} from 'lucide-react';
import { logoutUser } from '../store/userSlice';
import { useSidebar } from '../contexts/SidebarContext';
import LoginModal from './LoginModal';
import LanguageSwitcher from './LanguageSwitcher';

export default function PodcastSidebar() {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // 登录弹窗状态
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 获取用户信息
  const { user, isLoggedIn } = useSelector((state) => state.user);
  
  // 处理登录需求
  const handleLoginRequired = () => {
    setShowLoginModal(true);
  };
  
  // 处理登录成功
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
  };
  
  // 处理关闭登录弹窗
  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  // 监听语言变化，强制重新渲染
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChange);
    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // 文件系统管理模块
  const fileSystemFeatures = [
    { 
      name: t('podcastSidebar.fileSystem.fileManager', '文件管理器'), 
      href: '/podcast/sidebar/file-manager', 
      icon: BookOpen,
      requireAuth: true 
    },
    {
      name: t('podcastSidebar.fileSystem.uploads', '上传文件'),
      href: '/podcast/sidebar/uploads',
      icon: Upload,
      requireAuth: true
    },
    {
      name: t('podcastSidebar.fileSystem.audioLibrary', '音频库'),
      href: '/podcast/sidebar/audio-library',
      icon: Music,
      requireAuth: true
    },
    {
      name: t('podcastSidebar.fileSystem.projects', '项目文件'),
      href: '/podcast/sidebar/projects',
      icon: FolderOpen,
      requireAuth: true
    }
  ];

  // 播客制作工具模块
  const podcastTools = [
    {
      name: t('podcastSidebar.tools.recorder', '录音工具'),
      href: '/podcast/sidebar/recorder',
      icon: Mic,
      requireAuth: true
    },
    {
      name: t('podcastSidebar.tools.editor', '音频编辑'),
      href: '/podcast/sidebar/editor',
      icon: Radio,
      requireAuth: true
    },
    {
      name: t('podcastSidebar.tools.projects', '项目管理'),
      href: '/podcast/sidebar/project-manager',
      icon: Folder,
      requireAuth: true
    }
  ];

  // 检查当前路径是否匹配
  const isActiveRoute = (href) => {
    if (href === '#') return false;
    return location.pathname.startsWith(href);
  };

  // 处理登出
  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  // 渲染导航项
  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = isActiveRoute(item.href);
    
    return (
      <li key={item.name}>
        <Link
          to={item.href}
          className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group ${
            isActive
              ? 'bg-purple-100 text-purple-600 font-semibold'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          } ${isCollapsed ? 'justify-center' : ''}`}
          title={isCollapsed ? item.name : ''}
        >
          <Icon className={`w-5 h-5 flex-shrink-0 ${
            isActive ? 'text-purple-600' : 'text-gray-500 group-hover:text-gray-700'
          }`} />
          {!isCollapsed && (
            <span className="text-sm">{item.name}</span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <>
      <aside className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 shadow-lg transition-all duration-300 z-40 flex flex-col ${
        isCollapsed ? 'w-20' : 'w-64'
      }`}>
        {/* 顶部Logo区域 - 播客主题的紫色配色 */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 flex-shrink-0">
          {isCollapsed ? (
            // 收缩状态：Logo和按钮分两行
            <>
              <div className="h-16 flex items-center justify-center">
                <Link 
                  to="/podcast/sidebar"
                  className="flex items-center hover:opacity-80 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-md">
                    <Headphones className="w-6 h-6 text-purple-600" />
                  </div>
                </Link>
              </div>
              <div className="pb-3 flex justify-center">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-300"
                >
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            </>
          ) : (
            // 展开状态：Logo和按钮同一行
            <div className="h-16 flex items-center justify-between px-4">
              <Link 
                to="/podcast/sidebar"
                className="flex items-center hover:opacity-80 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-md">
                  <Headphones className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-3">
                  <h1 className="text-xl font-bold text-white">{t('podcastSidebar.brandName', 'AI 播客')}</h1>
                  <p className="text-xs text-white/80">{t('podcastSidebar.brandSlogan', '智能播客创作平台')}</p>
                </div>
              </Link>
              
              <button
                onClick={toggleSidebar}
                className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-300"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>

        {/* 导航菜单 - 占据剩余空间 */}
        <nav className="flex-1 overflow-y-auto py-4">
          {/* 主要功能模块 */}
          <div className="mb-6">
            {!isCollapsed && (
              <div className="px-6 mb-3 flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-purple-500" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('podcastSidebar.sections.fileSystem', '文件系统')}</span>
              </div>
            )}
            <ul className="space-y-1 px-3">
              {fileSystemFeatures.filter(item => !item.requireAuth || isLoggedIn).map(renderNavItem)}
            </ul>
          </div>

          {/* 分隔线 */}
          <div className={`mx-4 border-t border-gray-200 ${isCollapsed ? 'mx-2' : ''}`}></div>

          {/* 创作工具模块 */}
          <div className="mt-6">
            {!isCollapsed && (
              <div className="px-6 mb-3 flex items-center gap-2">
                <Radio className="w-4 h-4 text-green-500" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('podcastSidebar.sections.tools', '制作工具')}</span>
              </div>
            )}
            <ul className="space-y-1 px-3">
              {podcastTools.filter(item => !item.requireAuth || isLoggedIn).map(renderNavItem)}
            </ul>
          </div>

          {/* 返回主站按钮 */}
          <div className="mt-6">
            <ul className="space-y-1 px-3">
              <li>
                <Link
                  to="/ai-fleet"
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group text-gray-700 hover:bg-gray-100 hover:text-gray-900 ${
                    isCollapsed ? 'justify-center' : ''
                  }`}
                  title={isCollapsed ? t('podcastSidebar.actions.backToMain', '返回主站') : ''}
                >
                  <Home className="w-5 h-5 flex-shrink-0 text-gray-500 group-hover:text-gray-700" />
                  {!isCollapsed && (
                    <span className="text-sm">{t('podcastSidebar.actions.backToMain', '返回主站')}</span>
                  )}
                </Link>
              </li>
            </ul>
          </div>

          {/* 语言切换器 */}
          <div className="mt-6">
            <ul className="space-y-1 px-3">
              <li>
                <div className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group text-gray-700 hover:bg-gray-100 hover:text-gray-900 ${
                  isCollapsed ? 'justify-center' : ''
                }`}>
                  <Globe className="w-5 h-5 flex-shrink-0 text-gray-500 group-hover:text-gray-700" />
                  {!isCollapsed && (
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm">{t('common.language')}</span>
                      <LanguageSwitcher className="sidebar-inline-language-switcher" />
                    </div>
                  )}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {t('common.language')}
                      </div>
                    </div>
                  )}
                </div>
                {/* 收缩状态下的语言切换器 */}
                {isCollapsed && (
                  <div className="mt-1 flex justify-center">
                    <LanguageSwitcher className="sidebar-language-switcher collapsed" />
                  </div>
                )}
              </li>
            </ul>
          </div>
        </nav>

        {/* 底部整合模块 */}
        {isLoggedIn && user && (
          <div className="flex-shrink-0">
            {/* 充值升级按钮 - 播客主题的紫色渐变 */}
            <div className={`${isCollapsed ? 'px-2' : 'px-4'} pb-3`}>
              <button
                onClick={() => navigate('/shop')}
                className={`w-full relative overflow-hidden group ${
                  isCollapsed ? 'p-3' : 'px-5 py-3'
                }`}
              >
                {/* 渐变背景 */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-purple-600 to-pink-500 rounded-xl"></div>
                
                {/* 闪光效果 */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-xl"></div>
                
                {/* 内容 */}
                <div className="relative flex items-center justify-center gap-2">
                  <Sparkles className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-white`} />
                  {!isCollapsed && (
                    <>
                      <span className="text-white font-semibold text-sm">{t('podcastSidebar.actions.upgrade', '升级会员')}</span>
                      <Crown className="w-4 h-4 text-yellow-300" />
                    </>
                  )}
                </div>
              </button>
            </div>
            
            {/* 用户信息和设置 */}
            <div className={`border-t border-gray-200 bg-gradient-to-t from-gray-50 to-white ${
              isCollapsed ? 'p-2' : 'p-4'
            }`}>
              <div className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 ${
                isCollapsed ? 'p-2' : 'p-3'
              }`}>
                {/* 用户信息 */}
                <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
                  {/* 用户头像 */}
                  <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-500 rounded-lg flex items-center justify-center shadow-sm">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    {/* 在线状态指示器 */}
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                  </div>
                  
                  {/* 用户信息文字 */}
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {user.name || user.email || t('podcastSidebar.user.user', '用户')}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {(() => {
                          const uid = user.uid || user.id || user.email || '';
                          if (uid.length > 16) {
                            return `${uid.substring(0, 6)}...${uid.substring(uid.length - 6)}`;
                          }
                          return uid;
                        })()}
                      </p>
                    </div>
                  )}
                  
                  {/* 设置按钮 - 展开状态显示在右侧 */}
                  {!isCollapsed && (
                    <button
                      onClick={() => navigate('/settings')}
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-300"
                      title={t('podcastSidebar.actions.settings', '设置')}
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* 设置按钮 - 收缩状态单独一行 */}
                {isCollapsed && (
                  <button
                    onClick={() => navigate('/settings')}
                    className="mt-2 w-full p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-all duration-300 flex justify-center"
                    title={t('podcastSidebar.actions.settings', '设置')}
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* 未登录状态的底部 */}
        {!isLoggedIn && (
          <div className="flex-shrink-0 bg-gradient-to-t from-gray-50 to-white border-t border-gray-200">
            <div className={`${isCollapsed ? 'p-2' : 'p-4'}`}>
              <button
                onClick={handleLoginRequired}
                className={`w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300 ${
                  isCollapsed ? 'p-2' : 'px-4 py-3'
                }`}
              >
                {isCollapsed ? (
                  <User className="w-4 h-4 mx-auto" />
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="text-sm">{t('podcastSidebar.actions.loginRegister', '登录/注册')}</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* 登录弹窗 */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLoginSuccess}
      />
    </>
  );
}